<template>
  <canvas
    ref="photoCanvas"
    class="absolute inset-0 w-full h-full"
    :style="{
      zIndex: 5,
      pointerEvents: interactive ? 'auto' : 'none',
      clipPath: eggClipPath,
    }"
    @touchstart.prevent="onTouchStart"
    @touchmove.prevent="onTouchMove"
    @touchend.prevent="onTouchEnd"
    @touchcancel.prevent="onTouchEnd"
  ></canvas>
</template>

<script>
import { getEggClipPathCSS } from '@/utils/egg-shape';

export default {
  name: 'EggPhotoLayer',
  props: {
    photoImage: {
      type: String,
      default: null,
    },
    transform: {
      type: Object,
      default: () => ({ x: 0, y: 0, scale: 1, rotation: 0 }),
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    interactive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:transform'],
  data() {
    return {
      eggClipPath: getEggClipPathCSS(),
      loadedImage: null,
      touchState: null,
    };
  },
  watch: {
    photoImage() {
      this.loadImage();
    },
    transform: {
      deep: true,
      handler() {
        this.renderPhoto();
      },
    },
    width() {
      this.resizeCanvas();
    },
    height() {
      this.resizeCanvas();
    },
  },
  mounted() {
    this.resizeCanvas();
    if (this.photoImage) {
      this.loadImage();
    }
  },
  methods: {
    resizeCanvas() {
      const canvas = this.$refs.photoCanvas;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = this.width * dpr;
      canvas.height = this.height * dpr;
      canvas.style.width = `${this.width}px`;
      canvas.style.height = `${this.height}px`;

      this.renderPhoto();
    },

    loadImage() {
      if (!this.photoImage) {
        this.loadedImage = null;
        this.clearCanvas();
        return;
      }

      const img = new Image();
      img.onload = () => {
        this.loadedImage = img;
        this.renderPhoto();
      };
      img.src = this.photoImage;
    },

    clearCanvas() {
      const canvas = this.$refs.photoCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },

    renderPhoto() {
      const canvas = this.$refs.photoCanvas;
      if (!canvas || !this.loadedImage) {
        this.clearCanvas();
        return;
      }

      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.save();

      const t = this.transform;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      ctx.translate(centerX + t.x * dpr, centerY + t.y * dpr);
      ctx.rotate((t.rotation || 0) * (Math.PI / 180));
      ctx.scale(t.scale, t.scale);

      // Draw image centered, scaled to fill egg width
      const imgAspect = this.loadedImage.width / this.loadedImage.height;
      const canvasAspect = canvas.width / canvas.height;

      let drawWidth, drawHeight;
      if (imgAspect > canvasAspect) {
        // Image is wider: fit to height
        drawHeight = canvas.height;
        drawWidth = drawHeight * imgAspect;
      } else {
        // Image is taller: fit to width
        drawWidth = canvas.width;
        drawHeight = drawWidth / imgAspect;
      }

      ctx.drawImage(this.loadedImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

      ctx.restore();
    },

    onTouchStart(e) {
      if (!this.interactive) return;

      if (e.touches.length === 1) {
        this.touchState = {
          type: 'pan',
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTransform: { ...this.transform },
        };
      } else if (e.touches.length === 2) {
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const dy = e.touches[1].clientY - e.touches[0].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);

        this.touchState = {
          type: 'pinch',
          startDist: dist,
          startAngle: angle,
          startScale: this.transform.scale,
          startRotation: this.transform.rotation || 0,
          startX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          startY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          startTransform: { ...this.transform },
        };
      }
    },

    onTouchMove(e) {
      if (!this.touchState || !this.interactive) return;

      if (this.touchState.type === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - this.touchState.startX;
        const dy = e.touches[0].clientY - this.touchState.startY;

        this.$emit('update:transform', {
          x: this.touchState.startTransform.x + dx,
          y: this.touchState.startTransform.y + dy,
          scale: this.transform.scale,
          rotation: this.transform.rotation || 0,
        });
      } else if (this.touchState.type === 'pinch' && e.touches.length === 2) {
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const dy = e.touches[1].clientY - e.touches[0].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);

        const scaleRatio = dist / this.touchState.startDist;
        const newScale = Math.min(Math.max(this.touchState.startScale * scaleRatio, 0.3), 5);
        const newRotation = this.touchState.startRotation + (angle - this.touchState.startAngle);

        this.$emit('update:transform', {
          x: this.touchState.startTransform.x,
          y: this.touchState.startTransform.y,
          scale: newScale,
          rotation: newRotation,
        });
      }
    },

    onTouchEnd() {
      this.touchState = null;
    },

    getCanvas() {
      return this.$refs.photoCanvas;
    },
  },
};
</script>
